import React, {createContext, use, useEffect, useMemo, useRef, useState} from "react";
import PropTypes from "prop-types";
import axios from "axios";
import {LoginForm} from "../LoginForm";
import ForgotPasswordForm from "../ForgotPasswordForm";
import NewUserPasswordForm from "../NewUserPasswordForm";
import MFAChallengeForm from "../MFA/MFAChallengeForm";
import Loader from "../../Loader";
import {setCookieSession, getCookieSession, clearCookieSession} from "@breachrx/client-auth";
import {fromBase64URL} from "../../../resources/utils/encoding";
import retryWithBackoff from "../../../resources/utils/retryWithBackoff";
import {getAllProviderLogins} from "./authProvidersHelpers";
import {identity, isEmpty, isString, pick} from "underscore";

const AUTH_ENDPOINT = process.env.AUTH_ENDPOINT;

const AuthContext = createContext(undefined);

const challengeComponentMap = {
  NEW_PASSWORD_REQUIRED: <NewUserPasswordForm />,
  SMS_MFA: <MFAChallengeForm mfaMethod="SMS_MFA" />,
  SOFTWARE_TOKEN_MFA: <MFAChallengeForm mfaMethod="SOFTWARE_TOKEN_MFA" />,
};

const AuthProvider = ({children}) => {
  const orgName = window.location.hostname.split(".").shift();
  const [authSession, setAuthSession] = useState(getCookieSession());
  const [altAuthProviders, setAltAuthProviders] = useState();
  const [errorMessage, setErrorMessage] = useState("");
  const [isAuthLoading, setIsAuthLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const isAltAuthLoading = !altAuthProviders;
  const processedProviderCode = useRef(null);

  useEffect(() => {
    const fetchAuthProviders = async () => {
      if (altAuthProviders) {
        return;
      }
      try {
        const providers = await getAllProviderLogins(orgName);
        setAltAuthProviders(providers);
      } catch (error) {
        console.error("Failed to fetch auth providers:", error);
      }
    };

    fetchAuthProviders();
  }, []);

  const resetAuthProviders = async () => {
    const providers = await getAllProviderLogins(orgName);
    setAltAuthProviders(providers);
  };

  const login = async (data) => {
    const {username, password} = data;
    const cleanUserName = `${username}`.trim().toLowerCase();
    if (!cleanUserName) {
      setErrorMessage("Please enter a valid email address");
      return;
    }

    setIsAuthLoading(true);
    setErrorMessage("Logging in...");
    try {
      const response = await retryWithBackoff(() =>
        axios.post(
          `${AUTH_ENDPOINT}/login`,
          {orgName},
          {
            auth: {username: cleanUserName, password},
            headers: {"X-Client-Platform": "web"},
          },
        ),
      );

      if (response.status === 200) {
        setAuthSession({
          ...response.data,
          rememberMe: data.rememberMe,
          username,
        });
        setErrorMessage("");
      } else {
        setErrorMessage("Login unsuccessful.");
      }
    } catch (error) {
      if (error?.message.match(/426/i)) {
        setErrorMessage("One time configuration change. Please log in again.");
      } else {
        if ((error?.message ?? error.response?.data?.message) === "Network Error") {
          setErrorMessage("Login unsuccessful");
        } else {
          setErrorMessage(error.response?.data?.message ?? error?.message ?? "Invalid credentials");
        }
      }
    } finally {
      setIsAuthLoading(false);
    }
  };

  const logout = async () => {
    clearCookieSession();
    setAuthSession();
    processedProviderCode.current = null;
  };

  const refreshSession = async () => {
    setIsRefreshing(true);
    try {
      const response = await axios.post(
        `${AUTH_ENDPOINT}/token/refresh`,
        {
          orgName,
          AuthParameters: {
            REFRESH_TOKEN: authSession.RefreshToken,
            USERNAME: authSession.username,
          },
        },
        {
          timeout: 30000,
          headers: {"X-Client-Platform": "web"},
        },
      );

      if (response.status === 200) {
        setAuthSession({...authSession, ...response.data});
        return response.data;
      } else {
        logout();
      }
    } catch (error) {
      if (error?.response?.status === 400 || error?.response?.status === 401) {
        logout();
      } else if (!error?.response) {
        if (error.code === "ECONNABORTED") {
          setErrorMessage("Session refresh timed out. Please log in again.");
        }
        logout();
      }
    } finally {
      setIsRefreshing(false);
    }
  };

  const providerValues = {
    orgName,
    errorMessage,
    authSession,
    isAuthLoading,
    setIsAuthLoading,
    setAuthSession,
    setErrorMessage,
    login,
    refreshSession,
    logout,
    altAuthProviders,
    hasAuthProviders: !isEmpty(pick(altAuthProviders, identity)),
    resetAuthProviders,
    isAltAuthLoading,
  };

  useEffect(() => {
    const code = new URLSearchParams(window.location.search).get("code");
    if (code && !authSession && processedProviderCode.current !== code) {
      processedProviderCode.current = code;
      (async () => {
        try {
          const response = await axios.post(
            `${AUTH_ENDPOINT}/login/token`,
            {orgName, code},
            {headers: {"X-Client-Platform": "web"}},
          );
          const state = new URLSearchParams(window.location.search).get("state");
          const returnLink = !isEmpty(state) ? fromBase64URL(state) : window.location.pathname;
          window.history.replaceState(null, "", returnLink);
          const rememberMe = window.sessionStorage.getItem("rememberMe") === "true";
          setAuthSession({...response.data, rememberMe});
        } catch (error) {
          setErrorMessage(error.response?.data?.message || "Authentication failed");
          processedProviderCode.current = null;
        }
      })();
    }
  }, [authSession, orgName]);

  useEffect(() => {
    if (!authSession?.AccessToken) {
      return;
    }
    (async () => {
      try {
        const tokenPayload = JSON.parse(
          Buffer.from(authSession.AccessToken.split(".")[1], "base64").toString(),
        );
        const isExpired = tokenPayload.exp * 1000 < Date.now();
        if (isExpired) {
          await refreshSession();
        }
      } catch (error) {
        logout();
      }
    })();
  }, [authSession]);

  useEffect(() => {
    if (authSession?.AccessToken && authSession.rememberMe === true) {
      setCookieSession(authSession);
    } else if (authSession?.AccessToken) {
      const cookies = getCookieSession();
      if (cookies?.IdToken && cookies?.IdToken !== authSession.IdToken) {
        setCookieSession(authSession);
        window.location.reload();
      }
    }
  }, [authSession]);

  useEffect(() => {
    const errorInURL = new URLSearchParams(window.location.search).get("error_description");
    if (isString(errorInURL) && !isEmpty(errorInURL)) {
      setErrorMessage(errorInURL);
    }
  }, []);

  const loginComponent = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    const confirmationCode = params.get("confirmationCode");
    const code = params.get("code");
    if (confirmationCode && !authSession) {
      return <ForgotPasswordForm />;
    }
    if (!authSession && code && !errorMessage) {
      return <Loader />;
    }
    if (!authSession) {
      return <LoginForm />;
    }
    if (authSession.AccessToken) {
      return children;
    }
    if (authSession.ChallengeName) {
      return challengeComponentMap[authSession.ChallengeName];
    }
    return <Loader />;
  }, [authSession, children, errorMessage]);

  return (
    <AuthContext value={providerValues}>
      {isRefreshing ? <Loader fullViewingHeight /> : loginComponent}
    </AuthContext>
  );
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

const useAuth = () => {
  return use(AuthContext);
};

export {AuthContext, AuthProvider, useAuth};
